import { useState } from "react";
import { Link } from "react-router-dom";
import { Building2, ChevronRight, Filter, Power, Search } from "lucide-react";
import { useI18n } from "../../../i18n";
import { fmtMoney } from "../../../lib/format";
import { pct } from "../../../lib/xutil";
import { useApi } from "../../customer/hooks";
import { useAuth } from "../../../context/AuthContext";
import { hasPerm } from "../../../lib/perm";
import { PageHead, btnGhost, chip, input } from "../../../components/common/Page";
import { EmptyState, SkeletonBlock } from "../../../components/common/EmptyState";
import { BulkDialog, StatusBadge, StatusDialog } from "./StatusDialog";

const Bar = ({ v }) => (
  <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
    <div className={`h-full rounded-full ${v > 90 ? "bg-rose-500" : v > 70 ? "bg-amber-400" : "bg-brand"}`} style={{ width: `${Math.min(v || 0, 100)}%` }} />
  </div>
);

const Counters = ({ c, status, setStatus }) => {
  const { t } = useI18n();
  const cards = [["active", "cActive", "text-navy"], ["inactive", "cInactive", "text-slate-500"], ["invited", "cInvited", "text-sky-700"]];
  return (
    <div className="grid grid-cols-3 gap-2 sm:max-w-xl" data-testid="customer-status-counts">
      {cards.map(([k, label, cls]) => (
        <button key={k} type="button" disabled={k === "invited"} onClick={() => setStatus(status === k ? "all" : k)} data-testid={`count-${k}`}
          className={`rounded-2xl bg-white p-3 text-left shadow-sm transition-shadow disabled:cursor-default ${status === k ? "ring-2 ring-navy" : ""}`}>
          <p className="text-xs text-slate-500">{t(`cstat.${label}`)}</p><p className={`text-2xl font-bold tabular-nums ${cls}`} data-testid={`count-${k}-value`}>{c ? c[k] : "…"}</p>
        </button>))}
    </div>
  );
};

const Row = ({ c, fin, canStatus, sel, onSel, onToggle }) => {
  const { t } = useI18n();
  return (
    <li className={`flex items-center ${c.active ? "" : "bg-slate-50/70"}`}>
      {canStatus && <label className="grid h-14 w-11 shrink-0 cursor-pointer place-items-center pl-2"><input type="checkbox" checked={sel} onChange={() => onSel(c.code)} className="h-5 w-5 accent-[#002068]" data-testid={`customer-select-${c.code}`} /></label>}
      <Link to={`/staff/customers/${encodeURIComponent(c.code)}`} data-testid={`customer-row-${c.code}`} className={`grid min-w-0 flex-1 grid-cols-[1fr_auto] items-center gap-3 px-4 py-3.5 hover:bg-slate-50 ${fin ? "md:grid-cols-[1.6fr_1fr_1fr_1fr_auto]" : "md:grid-cols-[1fr_auto]"}`}>
        <div className="min-w-0"><p className={`truncate font-semibold ${c.active ? "text-navy" : "text-slate-500"}`}>{c.name}</p>
          <p className="flex min-w-0 items-center gap-2 truncate text-xs text-slate-500"><StatusBadge active={c.active} invited={c.invited} testId={`customer-status-${c.code}`} /><span className="truncate">{c.code} · {c.area} · {c.sales_rep}</span></p></div>
        {fin && <>
          <div className="hidden text-sm tabular-nums text-navy md:block"><span className="text-xs text-slate-500">{t("x.cust.balance")}</span><p>{fmtMoney(c.balance)}</p></div>
          <div className={`hidden text-sm tabular-nums md:block ${c.overdue > 0 ? "text-rose-600" : "text-slate-400"}`}><span className="text-xs text-slate-500">{t("x.cust.overdue")}</span><p>{fmtMoney(c.overdue)}</p></div>
          <div className="hidden md:block"><span className="text-xs text-slate-500">{t("x.cust.creditUsed")} {pct(c.credit_used_pct)}</span><Bar v={c.credit_used_pct} /></div>
          <div className="flex items-center gap-2 md:hidden"><span className={`text-sm font-semibold tabular-nums ${c.overdue > 0 ? "text-rose-600" : "text-navy"}`}>{fmtMoney(c.balance)}</span></div>
        </>}
        <ChevronRight className="hidden h-4 w-4 text-slate-300 md:block" />
      </Link>
      {canStatus && <button type="button" onClick={() => onToggle(c)} aria-label={t(c.active ? "cstat.deactivate" : "cstat.activate")} title={t(c.active ? "cstat.deactivate" : "cstat.activate")}
        className={`mr-2 grid h-10 w-10 shrink-0 place-items-center rounded-full transition-colors ${c.active ? "text-brand hover:bg-rose-50 hover:text-rose-600" : "text-slate-400 hover:bg-brand/15 hover:text-navy"}`} data-testid={`customer-toggle-${c.code}`}><Power className="h-4 w-4" /></button>}
    </li>
  );
};

const VISIBLE = 300;

export default function CustomersList() {
  const { t } = useI18n();
  const { user } = useAuth();
  const fin = hasPerm(user, "finance:read");
  const canStatus = hasPerm(user, "customers:status");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [sel, setSel] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [limit, setLimit] = useState(VISIBLE);
  const params = { ...(q.trim() ? { q: q.trim() } : {}), ...(status !== "all" ? { status } : {}) };
  const r = useApi("staffCustomers", "/staff/customers", params, { staleTime: 30000, placeholderData: (p) => p });
  const counts = useApi("custCounts", "/staff/customers/status-counts", undefined, { staleTime: 0 });
  const toggleSel = (code) => setSel((s) => (s.includes(code) ? s.filter((x) => x !== code) : [...s, code]));
  const done = () => { setDlg(null); setSel([]); r.refetch(); counts.refetch(); };
  const rows = r.data || [];
  return (
    <div className="space-y-5" data-testid="customers-page">
      <PageHead title={t("x.cust.title")} sub={t("x.cust.sub")}>
        {canStatus && <button type="button" onClick={() => setDlg({ bulk: true })} className={btnGhost} data-testid="bulk-status-open"><Filter className="h-4 w-4" />{t("cstat.bulk")}</button>}
      </PageHead>
      <Counters c={counts.data} status={status} setStatus={setStatus} />
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-md">
          <Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("x.cust.search")} className={`${input} pl-9`} data-testid="customers-search" />
        </div>
        {["active", "inactive", "all"].map((k) => <button key={k} type="button" className={chip(status === k)} onClick={() => setStatus(k)} data-testid={`status-filter-${k}`}>{t(k === "all" ? "cstat.all" : k === "active" ? "cstat.cActive" : "cstat.cInactive")}</button>)}
      </div>
      {sel.length > 0 && (
        <div className="sticky top-16 z-10 flex flex-wrap items-center gap-2 rounded-2xl bg-navy px-4 py-2.5 text-sm text-white shadow-lg" data-testid="bulk-bar">
          <span className="font-semibold" data-testid="bulk-selected">{t("cstat.selected", { n: sel.length })}</span>
          <button type="button" onClick={() => setDlg({ codes: sel, active: true })} className="h-9 rounded-full bg-brand px-4 font-semibold text-navy" data-testid="bulk-activate">{t("cstat.activate")}</button>
          <button type="button" onClick={() => setDlg({ codes: sel, active: false })} className="h-9 rounded-full bg-rose-600 px-4 font-semibold" data-testid="bulk-deactivate">{t("cstat.deactivate")}</button>
          <button type="button" onClick={() => setSel([])} className="ml-auto h-9 px-2 text-white/70 hover:text-white" data-testid="bulk-clear">{t("cstat.clear")}</button>
        </div>
      )}
      {r.isLoading ? <SkeletonBlock className="h-64" /> : !rows.length ? <EmptyState icon={Building2} title={t("x.cust.empty")} testId="customers-empty" /> : (
        <>
          {canStatus && <label className="flex items-center gap-2 px-1 text-xs text-slate-500"><input type="checkbox" className="h-4 w-4 accent-[#002068]" data-testid="select-all-visible"
            checked={rows.slice(0, limit).every((c) => sel.includes(c.code))} onChange={(e) => setSel(e.target.checked ? [...new Set([...sel, ...rows.slice(0, limit).map((c) => c.code)])] : [])} />{rows.length} · {t("cstat.all")}</label>}
          <ul className="divide-y divide-slate-100 overflow-hidden rounded-2xl bg-white shadow-sm" data-testid="customers-list">
            {rows.slice(0, limit).map((c) => <Row key={c.code} c={c} fin={fin} canStatus={canStatus} sel={sel.includes(c.code)} onSel={toggleSel} onToggle={(x) => setDlg({ codes: [x.code], active: !x.active })} />)}
          </ul>
          {rows.length > limit && <button type="button" onClick={() => setLimit((n) => n + VISIBLE)} className={`${btnGhost} w-full`} data-testid="customers-more">+ {Math.min(VISIBLE, rows.length - limit)} / {rows.length - limit}</button>}
        </>
      )}
      {dlg?.codes && <StatusDialog codes={dlg.codes} active={dlg.active} onClose={() => setDlg(null)} onDone={done} />}
      {dlg?.bulk && <BulkDialog meta={counts.data || {}} onClose={() => setDlg(null)} onDone={done} />}
    </div>
  );
}
